import type { Schemas } from '@npc/api-client'
import type { ReactNode } from 'react'

import { Card } from '../../components/card'
import { T } from '../../copy/text'
import { t } from '../../copy'

/**
 * 黑色凭证卡。设计稿里唯一的深色卡，因为它是这一屏最要紧的东西——
 * 第 1 号痛点「记不清 SSH 是多少」就是它解决的。
 *
 * 两条安全线画在这儿：
 *
 *   1. **平台保管的那把私钥，界面上永远只是一排点。** 看原始密钥是红级动作，
 *      要当场再验一次 Passkey（M1-19），这一屏不提供绕过它的路
 *   2. 「复制」复制的是**临时钥匙**，不是那把长期的。签发走
 *      `POST /hosts/{id}/access`，默认 2 小时到期自动作废，全程审计。
 *      所以它在签发之前是禁用的——没有临时钥匙的时候，这里没有任何东西可复制
 *
 * 临时私钥只在 `ServerView` 的 state 里待着：不进 localStorage，
 * 也不进 TanStack Query 的缓存（契约里写明「前端不许把它写进 localStorage」）。
 */
export function CredentialCard({
  host,
  access,
}: {
  host: Schemas['Host']
  access: Schemas['TemporaryAccess'] | null
}) {
  async function copyTemporaryKey() {
    const key = access?.private_key
    if (!key) return
    await navigator.clipboard?.writeText(key)
  }

  return (
    <Card tone="dark" className="border-none">
      <div className="mb-npc-gap-lg flex items-center justify-between">
        <div className="text-npc-section-sm font-semibold">
          <T id="host.credential.heading" />
        </div>
        <span className="rounded-npc-chip-inline border border-npc-dark-avatar px-npc-gap-xs py-[2px] text-npc-meta-sm text-npc-dark-ink-softer">
          <T id="host.credential.kept" />
        </span>
      </div>

      <dl className="m-0 flex flex-col gap-npc-gap font-npc-mono text-npc-caption">
        <Field label={<T id="host.credential.field.address" />}>
          {host.address} : {host.ssh_port}
        </Field>
        <Field label={<T id="host.credential.field.user" />}>{host.ssh_user}</Field>
        <Field label={<T id="host.credential.field.key" />}>
          <span aria-label={t('host.credential.kept')}>••••••••••••••••••••••</span>{' '}
          <button
            type="button"
            disabled={!access}
            onClick={() => void copyTemporaryKey()}
            className="cursor-pointer border-none bg-transparent p-0 font-[inherit] text-npc-green-muted disabled:cursor-not-allowed disabled:text-npc-dark-ink-green"
          >
            <T id="host.credential.copy" />
          </button>
        </Field>
      </dl>

      <div className="mt-[15px] border-t border-npc-dark-border-2 pt-npc-grid-sm font-npc-sans text-npc-caption-sm leading-[1.6] text-npc-dark-ink-soft">
        <T id="host.credential.note" />
      </div>
    </Card>
  )
}

function Field({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div>
      <dt className="mb-[3px] font-npc-sans text-npc-meta-sm text-npc-dark-ink-green">{label}</dt>
      <dd className="m-0">{children}</dd>
    </div>
  )
}
